// Fase 7 — capa de metadatos SEO.
//
// Diseño deliberado: las funciones `calcular*` son puras (reciben datos,
// devuelven un objeto `MetadatosPagina`, no tocan `window`/`document`).
// Solo `aplicarMetadatos` toca el DOM. Esta separación es la que permite
// que, cuando llegue la Fase 8/9 (`publicar_tienda`), el generador de HTML
// estático reutilice exactamente las mismas funciones `calcular*` para
// producir el `<head>` de cada página pre-renderizada — sin reescribir la
// lógica de fallback ni la de datos estructurados. Ver también
// seo-estatico.js (robots.txt / sitemap.xml), que sigue el mismo principio.

const MARCA_JSONLD = 'sp-jsonld';

/**
 * @typedef {object} MetadatosPagina
 * @property {string} titulo
 * @property {string} descripcion
 * @property {string} canonical - URL absoluta
 * @property {'index,follow'|'noindex,follow'|'noindex,nofollow'} robots
 * @property {{ tipo: string, imagen?: string }} openGraph
 * @property {object[]} jsonLd - lista de objetos schema.org (se serializan tal cual)
 */

/**
 * Recorta un texto a un largo razonable para meta description (evita que
 * descripciones largas de producto rompan el snippet en buscadores).
 * @param {string} texto
 * @param {number} [limite]
 */
function recortar(texto, limite = 160) {
  const limpio = String(texto ?? '').trim();
  if (limpio.length <= limite) return limpio;
  return `${limpio.slice(0, limite - 1).trimEnd()}…`;
}

/**
 * @param {string} origen - protocolo + host, ej. https://demo.tuplataforma.pe
 * @param {string} ruta - ej. /catalogo o /producto/arroz-extra-5kg
 */
function construirCanonical(origen, ruta) {
  return `${origen.replace(/\/$/, '')}${ruta}`;
}

function imagenDestacada(tienda) {
  return (
    tienda.productos.find((producto) => producto.es_destacado)?.imagenes?.[0]?.url ??
    tienda.perfil.logo_url ??
    ''
  );
}

/**
 * @param {object} tienda
 * @param {string} origen
 * @returns {object} JSON-LD Organization
 */
function jsonLdOrganizacion(tienda, origen) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: tienda.empresa.nombre,
    url: origen,
    ...(tienda.perfil.logo_url ? { logo: tienda.perfil.logo_url } : {}),
    ...(tienda.perfil.descripcion ? { description: tienda.perfil.descripcion } : {}),
    ...(tienda.perfil.whatsapp
      ? {
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: tienda.perfil.whatsapp,
            contactType: 'customer service',
          },
        }
      : {}),
  };
}

/**
 * WebSite + SearchAction: el catálogo ya soporta `?q=` como búsqueda, así
 * que podemos declarar la acción de búsqueda real (sección 22.4).
 * @param {object} tienda
 * @param {string} origen
 */
function jsonLdSitioConBusqueda(tienda, origen) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: tienda.empresa.nombre,
    url: origen,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${origen}/catalogo?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * @param {{ nombre: string, url: string }[]} pasos
 */
function jsonLdMigaDePan(pasos) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: pasos.map((paso, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      name: paso.nombre,
      item: paso.url,
    })),
  };
}

/**
 * @param {object} tienda
 * @param {string} origen
 */
export function calcularMetaHome(tienda, origen) {
  const titulo = tienda.seo.meta_titulo ?? `${tienda.empresa.nombre} | Tienda online`;
  const descripcion = recortar(
    tienda.seo.meta_descripcion ??
      tienda.perfil.descripcion ??
      `Compra en línea en ${tienda.empresa.nombre}.`,
  );

  return {
    titulo,
    descripcion,
    canonical: construirCanonical(origen, '/'),
    robots: 'index,follow',
    openGraph: { tipo: 'website', imagen: imagenDestacada(tienda) },
    jsonLd: [jsonLdOrganizacion(tienda, origen), jsonLdSitioConBusqueda(tienda, origen)],
  };
}

/**
 * @param {object} tienda
 * @param {URLSearchParams} params
 * @param {object|undefined} categoriaActual
 * @param {string} origen
 */
export function calcularMetaCatalogo(tienda, params, categoriaActual, origen) {
  const busqueda = params.get('q') ?? '';
  const ruta = `/catalogo${categoriaActual ? `?categoria=${categoriaActual.slug}` : ''}`;

  // Fallback (sección 22.3): categoría → nombre de categoría + empresa.
  // Las búsquedas y combinaciones de filtros no tienen fallback propio:
  // canonicalizan a la variante "limpia" (categoría sola, o catálogo sin
  // filtros) para no fragmentar autoridad SEO entre URLs casi idénticas
  // (sección 22.6).
  const titulo = categoriaActual
    ? (categoriaActual.seo?.meta_titulo ?? `${categoriaActual.nombre} | ${tienda.empresa.nombre}`)
    : `Catálogo | ${tienda.empresa.nombre}`;
  const descripcion = recortar(
    categoriaActual
      ? (categoriaActual.seo?.meta_descripcion ??
          categoriaActual.descripcion ??
          `Productos de ${categoriaActual.nombre} en ${tienda.empresa.nombre}.`)
      : `Explora todo el catálogo de ${tienda.empresa.nombre}.`,
  );

  const migaDePan = [
    { nombre: 'Inicio', url: construirCanonical(origen, '/') },
    { nombre: 'Catálogo', url: construirCanonical(origen, '/catalogo') },
  ];
  if (categoriaActual)
    migaDePan.push({ nombre: categoriaActual.nombre, url: construirCanonical(origen, ruta) });

  return {
    titulo,
    descripcion,
    canonical: construirCanonical(origen, ruta),
    // Una búsqueda de texto libre no aporta valor a un buscador y puede
    // generar contenido casi-duplicado infinito: se marca noindex.
    robots: busqueda ? 'noindex,follow' : 'index,follow',
    openGraph: { tipo: 'website', imagen: categoriaActual?.imagen_url ?? imagenDestacada(tienda) },
    jsonLd: [jsonLdMigaDePan(migaDePan)],
  };
}

/**
 * @param {object} tienda
 * @param {object} producto
 * @param {string} origen
 */
export function calcularMetaProducto(tienda, producto, origen) {
  const ruta = `/producto/${producto.slug}`;
  const categoriaPrincipal = producto.categorias?.[0];
  const imagen =
    producto.imagenes?.find((img) => img.es_principal)?.url ?? producto.imagenes?.[0]?.url ?? '';

  // Fallback (sección 22.3): nombre + precio + empresa.
  const titulo = producto.seo?.meta_titulo ?? `${producto.nombre} | ${tienda.empresa.nombre}`;
  const descripcion = recortar(
    producto.seo?.meta_descripcion ??
      producto.descripcion_corta ??
      `${producto.nombre} a ${producto.precio} ${tienda.empresa.moneda} en ${tienda.empresa.nombre}.`,
  );

  const migaDePan = [
    { nombre: 'Inicio', url: construirCanonical(origen, '/') },
    { nombre: 'Catálogo', url: construirCanonical(origen, '/catalogo') },
  ];
  if (categoriaPrincipal) {
    migaDePan.push({
      nombre: categoriaPrincipal.nombre,
      url: construirCanonical(origen, `/catalogo?categoria=${categoriaPrincipal.slug}`),
    });
  }
  migaDePan.push({ nombre: producto.nombre, url: construirCanonical(origen, ruta) });

  const jsonLdProducto = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: producto.nombre,
    ...(imagen ? { image: [imagen] } : {}),
    ...(producto.descripcion_corta ? { description: producto.descripcion_corta } : {}),
    sku: producto.id,
    offers: {
      '@type': 'Offer',
      priceCurrency: tienda.empresa.moneda,
      price: Number(producto.precio).toFixed(2),
      // No hay control de stock en el contrato publicado todavía: un
      // producto listado se asume disponible (disponibilidad genérica,
      // sección 22.4). Cuando exista stock real, este valor debe derivarse
      // de él en vez de asumirse.
      availability: 'https://schema.org/InStock',
      url: construirCanonical(origen, ruta),
    },
  };

  return {
    titulo,
    descripcion,
    canonical: construirCanonical(origen, ruta),
    robots: 'index,follow',
    openGraph: { tipo: 'product', imagen },
    jsonLd: [jsonLdProducto, jsonLdMigaDePan(migaDePan)],
  };
}

/**
 * @param {object} tienda
 * @param {string} origen
 */
export function calcularMetaContacto(tienda, origen) {
  const titulo = `Contacto | ${tienda.empresa.nombre}`;
  const descripcion = recortar(
    tienda.perfil.descripcion
      ? `Escríbele a ${tienda.empresa.nombre}: ${tienda.perfil.descripcion}`
      : `Datos de contacto y atención de ${tienda.empresa.nombre}.`,
  );

  /** @type {object} */
  const jsonLdNegocio = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: tienda.empresa.nombre,
    ...(tienda.perfil.direccion ? { address: tienda.perfil.direccion } : {}),
    ...(tienda.perfil.whatsapp ? { telephone: tienda.perfil.whatsapp } : {}),
    ...(tienda.perfil.correo ? { email: tienda.perfil.correo } : {}),
    url: construirCanonical(origen, '/contacto'),
  };

  return {
    titulo,
    descripcion,
    canonical: construirCanonical(origen, '/contacto'),
    robots: 'index,follow',
    openGraph: { tipo: 'website', imagen: tienda.perfil.logo_url ?? '' },
    jsonLd: [jsonLdNegocio],
  };
}

/**
 * El carrito es una vista transaccional dependiente de estado local
 * (localStorage) sin contenido único que indexar: se excluye de buscadores.
 * @param {object} tienda
 * @param {string} origen
 */
export function calcularMetaCarrito(tienda, origen) {
  return {
    titulo: `Carrito | ${tienda.empresa.nombre}`,
    descripcion: 'Revisa tu selección y envía la consulta por WhatsApp.',
    canonical: construirCanonical(origen, '/carrito'),
    robots: 'noindex,follow',
    openGraph: { tipo: 'website', imagen: '' },
    jsonLd: [],
  };
}

/** @param {string} origen */
export function calcularMetaNoEncontrado(origen) {
  return {
    titulo: 'Página no encontrada',
    descripcion: 'La página que buscas ya no está disponible.',
    canonical: construirCanonical(origen, '/'),
    robots: 'noindex,follow',
    openGraph: { tipo: 'website', imagen: '' },
    jsonLd: [],
  };
}

/** @param {string} selector */
function actualizarMeta(selector, atributo, valor) {
  if (!valor) {
    document.querySelector(selector)?.remove();
    return;
  }
  let elemento = document.querySelector(selector);
  if (!elemento) {
    elemento = document.createElement('meta');
    const [, nombreAtributo, nombreValor] = selector.match(/meta\[(\w+)="([^"]+)"\]/) ?? [];
    if (nombreAtributo) elemento.setAttribute(nombreAtributo, nombreValor);
    document.head.appendChild(elemento);
  }
  elemento.setAttribute(atributo, valor);
}

/**
 * Aplica un objeto de metadatos al `document` actual: title, meta
 * description, robots, canonical, Open Graph, Twitter Card y JSON-LD.
 * Único punto de esta capa que toca el DOM.
 * @param {MetadatosPagina} meta
 */
export function aplicarMetadatos(meta) {
  document.title = meta.titulo;

  actualizarMeta('meta[name="description"]', 'content', meta.descripcion);
  actualizarMeta('meta[name="robots"]', 'content', meta.robots);

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', meta.canonical);

  actualizarMeta('meta[property="og:type"]', 'content', meta.openGraph.tipo);
  actualizarMeta('meta[property="og:title"]', 'content', meta.titulo);
  actualizarMeta('meta[property="og:description"]', 'content', meta.descripcion);
  actualizarMeta('meta[property="og:url"]', 'content', meta.canonical);
  actualizarMeta('meta[property="og:image"]', 'content', meta.openGraph.imagen ?? '');

  actualizarMeta(
    'meta[name="twitter:card"]',
    'content',
    meta.openGraph.imagen ? 'summary_large_image' : 'summary',
  );
  actualizarMeta('meta[name="twitter:title"]', 'content', meta.titulo);
  actualizarMeta('meta[name="twitter:description"]', 'content', meta.descripcion);
  actualizarMeta('meta[name="twitter:image"]', 'content', meta.openGraph.imagen ?? '');

  document.querySelectorAll(`script[data-${MARCA_JSONLD}]`).forEach((nodo) => nodo.remove());
  meta.jsonLd.forEach((bloque) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.spJsonld = '';
    script.textContent = JSON.stringify(bloque);
    document.head.appendChild(script);
  });
}
